import {useRef, useState} from 'react'
import {AnchoredOverlay, BranchName, Button, NavList} from '@primer/react'
import {DotFillIcon, DotIcon, PlusIcon, StackIcon} from '@primer/octicons-react'
import {getBaseBranchText} from '../utils/branch-label'
import type {StackData} from '../page-data/payloads/header'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useUnstackPullRequestsMutation} from '../mutations/use-unstack-pull-requests-mutation'
import {useQueryClient} from '@github-ui/react-query'
import {useHeaderPageDataQueryKey} from '../page-data/loaders/use-header-page-data'
import {UnstackDialog} from './UnstackButtonDialog'
import styles from './StackState.module.css'

type StackStateProps = {
  pullRequestNumber: number
  stack: StackData | undefined | null
}

export function StackState({pullRequestNumber, stack}: StackStateProps) {
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     TODO: Migrate to isFeatureEnabled from @github-ui/feature-flags. See
     https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
  const isPullRequestStacksEnabled = useFeatureFlag('pull_request_stacks')
  const [open, setOpen] = useState<boolean>(false)
  const [isConfirmingUnstack, setIsConfirmingUnstack] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState('')
  const unstackButtonRef = useRef<HTMLButtonElement>(null)

  const queryClient = useQueryClient()
  const headerPageDataQueryKey = useHeaderPageDataQueryKey()
  const {mutate: mutateUnstackPullRequests, isPending: isLoading} = useUnstackPullRequestsMutation()

  if (!isPullRequestStacksEnabled) {
    return null
  }

  if (!stack) {
    return null
  }

  const {baseBranch, repositoryOwnerLogin, repositoryName, position, size, pulls} = stack
  const baseBranchText = getBaseBranchText(baseBranch, repositoryOwnerLogin, repositoryOwnerLogin)

  // Pulls array is ordered from head to base, so top pull in the stack is the first item in the array.
  const topPullInStack = pulls[0]
  const addToStackBaseBranch = topPullInStack?.headBranch ?? baseBranch
  const addToStackHeadBranch = addToStackBaseBranch // Use same branch for now, until we have a "smarter" way of determining head ref for new PR
  const addToStackURL = `/${repositoryOwnerLogin}/${repositoryName}/compare/${addToStackBaseBranch}...${addToStackHeadBranch}?stack=true`

  return (
    <div className="d-flex flex-items-center fgColor-muted">
      <AnchoredOverlay
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        renderAnchor={props => (
          <Button
            className={styles.stackBadge}
            variant="invisible"
            leadingVisual={StackIcon}
            aria-label={`Navigate pull request stack, position ${position} of ${size}`}
            {...props}
          >
            {position}/{size}
          </Button>
        )}
      >
        <div>
          <NavList className={styles.stackNavList}>
            {pulls.map(stackedPR => {
              const isCurrent = stackedPR.number === pullRequestNumber
              return (
                <NavList.Item key={stackedPR.number} href={stackedPR.url} aria-current={isCurrent ? 'page' : undefined}>
                  <NavList.LeadingVisual>
                    {isCurrent ? <DotFillIcon className="fgColor-accent" /> : <DotIcon />}
                  </NavList.LeadingVisual>
                  <span className="fgColor-muted">#{stackedPR.number}</span>{' '}
                  <span className="text-bold">{stackedPR.title}</span>
                </NavList.Item>
              )
            })}
            <li className={styles.branchItem}>
              <span className={styles.branchItemContent}>
                <DotIcon className="fgColor-muted" />
                <BranchName as="span">{baseBranchText}</BranchName>
              </span>
            </li>
          </NavList>
          <div className="border-top" />
          <div className="d-flex flex-items-center flex-justify-between gap-2 px-2 py-1">
            <Button
              variant="invisible"
              size="small"
              className="fgColor-muted"
              leadingVisual={PlusIcon}
              as="a"
              href={addToStackURL}
            >
              Add
            </Button>
            <Button
              ref={unstackButtonRef}
              variant="invisible"
              size="small"
              className="fgColor-muted"
              onClick={() => setIsConfirmingUnstack(true)}
            >
              Unstack
            </Button>
          </div>
        </div>
      </AnchoredOverlay>
      <UnstackDialog
        unstackButtonRef={unstackButtonRef}
        isConfirmingUnstack={isConfirmingUnstack}
        isInactive={stack.pulls.some(pull => !pull.isUnstackable)}
        isLoading={isLoading}
        errorMessage={errorMessage}
        onCancelUnstack={() => {
          setIsConfirmingUnstack(false)
          setErrorMessage('')
        }}
        onConfirmUnstack={() => {
          setErrorMessage('')
          mutateUnstackPullRequests(
            {stackId: stack.id},
            {
              onError: error => setErrorMessage(error.message),
              onSuccess: async () => {
                try {
                  await queryClient.refetchQueries({queryKey: headerPageDataQueryKey}, {throwOnError: true})
                  setIsConfirmingUnstack(false)
                  setOpen(false)
                } catch {
                  setErrorMessage('Something unexpected happened. Please refresh the page.')
                }
              },
            },
          )
        }}
      />
    </div>
  )
}
